import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import logo from '../assets/logo.png';
import '../styles/shared.css';
import '../styles/project.css';
import { uploadToBlob, downloadFromBlob } from '../utils/blobStorage';

function Project() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentContent, setCurrentContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const projectId = new URLSearchParams(location.search).get('project');
  const quillRef = useRef(null);
  const [isQuillInitialized, setIsQuillInitialized] = useState(false);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoading(true);
        const userId = accounts[0]?.localAccountId;

        if (!userId) {
          console.log('No user ID found in accounts');
          return;
        }

        const response = await fetch(`/api/getUser?id=${encodeURIComponent(userId)}`);
        
        if (!response.ok) throw new Error('Failed to fetch user data');
        
        const userData = await response.json();
        setUserInfo(userData);
        
        // Find the specific project and its files
        const project = userData.projects.find(p => p.id === projectId);
        if (project) {
          setCurrentProject(project);
          setFiles(project.files || []);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (projectId) {
      fetchUserInfo();
    }
  }, [projectId, accounts]);

  // Handle file selection and Quill initialization
  const handleFileSelect = async (file) => {
    try {
      setIsLoading(true);
      setSelectedFile(file);

      const content = await downloadFromBlob(file.name, projectId);

      // Initialize Quill if it's not initialized yet
      if (!isQuillInitialized) {
        const editorContainer = document.getElementById('editor');
        if (editorContainer) {
          quillRef.current = new Quill('#editor', {
            theme: 'snow',
            modules: {
              toolbar: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'align': [] }],
                ['link', 'image', 'code-block'],
                ['clean']
              ]
            }
          });

          quillRef.current.on('text-change', () => {
            setCurrentContent(quillRef.current.root.innerHTML);
          });

          setIsQuillInitialized(true);
        }
      }

      // Update content in Quill editor
      if (quillRef.current) {
        quillRef.current.root.innerHTML = content;
      }
      setCurrentContent(content);
    } catch (error) {
      console.error('Error loading file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (quillRef.current) {
        quillRef.current = null;
        setIsQuillInitialized(false);
      }
    };
  }, []);

  const handleSave = async () => {
    if (!selectedFile) return;

    try {
      setIsLoading(true);
      
      // Only save to blob storage
      await uploadToBlob(currentContent, selectedFile.name, projectId);

    } catch (error) {
      console.error('Error saving file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFile = async () => {
    const fileName = prompt('Enter file name:');
    if (!fileName) return;

    try {
      setIsLoading(true);
      
      // Create empty file in blob storage
      await uploadToBlob('', `${fileName}.docx`, projectId);

      // Only add file metadata to database
      const response = await fetch(`/api/updateUser/${accounts[0]?.localAccountId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'addFile',
          data: {
            projectId,
            id: Date.now().toString(),
            name: `${fileName}.docx`
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to add file');
      const userData = await response.json();
      const project = userData.projects.find(p => p.id === projectId);
      setFiles(project.files || []);
    } catch (error) {
      console.error('Error adding file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  return (
    <div className="container">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      <nav className="nav">
        <img src={logo} alt="ExcelEA Logo" className="logo" />
        <div className="user-section">
          <span className="username">
            Welcome, {userInfo?.name || 'User'}
          </span>
          <button onClick={handleLogout} className="button button-danger">
            Sign Out
          </button>
        </div>
      </nav>

      <div className="content">
        {currentProject && (
          <div className="project-info">
            <div className="project-info-header">
              <h1 className="project-info-title">{currentProject.name}</h1>
              <button 
                onClick={() => navigate('/dashboard')}
                className="button button-secondary"
              >
                Back to Dashboard
              </button>
            </div>
            <p className="project-info-description">{currentProject.description}</p>
          </div>
        )}

        <div className="project-layout">
          <div className={`file-panel ${isPanelCollapsed ? 'collapsed' : ''}`}>
            <div className="panel-header">
              <h2>Files</h2>
              <button 
                className="collapse-button"
                onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              >
                {isPanelCollapsed ? '→' : '←'}
              </button>
            </div>
            <button 
              onClick={handleAddFile}
              className="button button-primary add-file-button"
            >
              Add File
            </button>
            <div className="file-list">
              {files.map(file => (
                <div 
                  key={file.id}
                  className={`file-item ${selectedFile?.id === file.id ? 'selected' : ''}`}
                  onClick={() => handleFileSelect(file)}
                >
                  <span className="file-name">{file.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="editor-container">
            {selectedFile ? (
              <>
                <div className="editor-header">
                  <h2>{selectedFile.name}</h2>
                  <button 
                    onClick={handleSave}
                    className="button button-primary"
                  >
                    Save
                  </button>
                </div>
                <div id="editor"></div>
              </>
            ) : (
              <div className="no-file-selected">
                <p>Select a file to edit</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Project; 